import React, { useState, useMemo, useCallback } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    FilterHeader,
    FilterHeading,
    Reset,
    FilterContent,
    FilterPopup,
} from './index.styled';
import CommonFilter from 'src/common/refactoredComponents/filter/CommonFilter.jsx';
import BrandFilter from 'src/common/refactoredComponents/BrandFilter/index.jsx';
import Button from 'src/common/refactoredComponents/button/Button';
import BottomNavigation from 'src/common/refactoredComponents/bottomNavigation/BottomNavigation';
import { ButtonStyles } from 'src/constants/buttonStyles';
import { useViewportHeight } from 'src/utils/useViewportHeight';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';
import { 
    checkNonDefault,
    initFilterView, 
    buildFilterState, 
    toggleSingleSelectFilter,
    toggleMultiSelectFilter 
} from 'src/utils/util';
import LocationFilter from './LocationFilter';
import {
    getInventoryList,
} from 'src/pages/receiveSets/redux/actions';
import { setSetsFilterData } from 'src/pages/receiveSets/redux/actions/index.js';
import { fetchFilterData } from 'src/pages/receiveSets/redux/actions';
import { setSetsSearchbarText } from 'src/pages/receiveSets/redux/actions/index.js';

const SetsFilterContainer = ({ onClose, bannerHeight = 0 , isFilterApplied}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const searchbarText = useSelector((state) => state.inventoryPreferenceReducer.searchbarText || {}, shallowEqual);
    const viewportHeight = useViewportHeight();
    const { districtFilters, territoryFilters , accountFilters, scopedAccountFilters = {}} = useSelector(
        (state) => state.consignedInventoryReducer.filterData || {},
        shallowEqual
    );
    const { brandFilterList, shelfLifeFilterList } = useSelector(
            (state) => state.inventoryPreferenceReducer.data || {},
            shallowEqual
        );
    const {currentDistrict, brand, shellLife, currentAccountId, homeAccountId, currentTerritoryId, homeTerritoryId } = useSelector(
        (state) => state.inventoryPreferenceReducer.setsFilterData || {},
        shallowEqual
    );
    const setsSearchParams = useSelector(
        (state) => state.inventoryPreferenceReducer.setsSearchParams || {},
        shallowEqual
    );

    const homeAccountFilters = scopedAccountFilters.homeLocationAccount || accountFilters;
    const currentAccountFilters = scopedAccountFilters.currentLocationAccount || accountFilters;
 
    // Initialize filter views using mock data
    const initDistrictView = (list) => initFilterView(list, 'All');
    const initBrandView = (list) => initFilterView(list, 'All');
    const initAllocationStatusView = (list) => initFilterView(list, 'All');

    const [filterState, setFilterState] = useState({
        DistrictView: initDistrictView(districtFilters),
        BrandView: initBrandView(brandFilterList),
        AllocationStatusView: initAllocationStatusView(shelfLifeFilterList),
        homeLocationTerritory: homeTerritoryId || (territoryFilters.length > 0 ? territoryFilters[0].filterCode : 'All'),
        homeLocationAccount: homeAccountId || (accountFilters.length > 0 ? accountFilters[0].filterCode : 'All'),
        currentLocationTerritory:  currentTerritoryId || (territoryFilters.length > 0 ? territoryFilters[0].filterCode : 'All'),
        currentLocationAccount: currentAccountId || (accountFilters.length > 0 ? accountFilters[0].filterCode : 'All'),
    });

    const [preFilterState, setPreFilterState] = useState({ ...filterState });

    const [expandedViews, setExpandedViews] = useState({
        DistrictView: false,
        BrandView: false,
        AllocationStatusView: false,
        HomeLocationView: false,
        CurrentLocationView: false,
    });

    const updateExpandedViews = ({ view, value }) => {
        setExpandedViews((prev) => ({
            ...prev,
            [view]: value,
        }));
    };

    const onCheckBoxChange = (event, filterName) => {
        const clickedValue = event.target.value;

        if (filterName === 'DistrictView') {
            setFilterState(prev => ({
                ...prev,
                DistrictView: toggleSingleSelectFilter(prev.DistrictView, clickedValue)
            }));
        const objTerritory = clickedValue !== 'All' ? {districtCode: clickedValue } : {};
        dispatch(fetchFilterData('territoryFilters', objTerritory));
        // Home and current account both reset to the same district-only query, so one call covers both scopes
        dispatch(fetchFilterData(
            'accountFilters',
            clickedValue !== 'All' ? {district: clickedValue } : {},
            ['homeLocationAccount', 'currentLocationAccount']
        ));
                 setFilterState(prev => ({
            ...prev,
            "currentLocationAccount": "All",
            "currentLocationTerritory": "All",
            "homeLocationAccount": "All",
            "homeLocationTerritory": "All"
        }));

        } else if (filterName === 'BrandView') {
            setFilterState(prev => ({
                ...prev,
                BrandView: toggleMultiSelectFilter(prev.BrandView, clickedValue, 'All')
            }));
        } else if (filterName === 'AllocationStatusView') {
            setFilterState(prev => ({
                ...prev,
                AllocationStatusView: toggleSingleSelectFilter(prev.AllocationStatusView, clickedValue)
            }));
        }
    };

    const handleLocationDropdownChange = (field, value) => {
        const nextValue = value ?? 'All';
        const selectedDistrictCode = filterState?.DistrictView.find(item => item.isChecked && item.filterCode !== 'All')?.filterCode;

        if(field === 'homeLocationTerritory') {
            dispatch(fetchFilterData(
                'accountFilters',
                {
                    ...(selectedDistrictCode ? { district: selectedDistrictCode } : {}),
                    ...(nextValue !== 'All' ? { territory: nextValue } : {}),
                },
                'homeLocationAccount'
            ));
             setFilterState(prev => ({
            ...prev,
            "homeLocationAccount": "All"
        }));
        }
        if(field === 'currentLocationTerritory'){
            dispatch(fetchFilterData(
                'accountFilters',
                {
                    ...(selectedDistrictCode ? { district: selectedDistrictCode } : {}),
                    ...(nextValue !== 'All' ? { territory: nextValue } : {}),
                },
                'currentLocationAccount'
            ));
             setFilterState(prev => ({
            ...prev,
            "currentLocationAccount": "All"
        }));
        }
        setFilterState(prev => ({
            ...prev,
            [field]: nextValue
        }));
    };

    const handleReset = useCallback(() => {
        const resetState = {
            DistrictView: initDistrictView(districtFilters),
            BrandView: initBrandView(brandFilterList),
            AllocationStatusView: initAllocationStatusView(shelfLifeFilterList),
            homeLocationTerritory: 'All',
            homeLocationAccount: 'All',
            currentLocationTerritory: 'All',
            currentLocationAccount: 'All',
        };
        setFilterState(resetState);
        setPreFilterState(resetState);
        dispatch(fetchFilterData('territoryFilters'));
        dispatch(fetchFilterData('accountFilters'));
        dispatch(fetchFilterData('accountFilters', {}, ['homeLocationAccount', 'currentLocationAccount']));
        dispatch(getInventoryList(setsSearchParams));
        dispatch(setSetsFilterData({}));
        onClose();
    }, [
        brandFilterList,
        dispatch,
        districtFilters,
        onClose,
        setsSearchParams,
        shelfLifeFilterList,
    ]);

    const handleApply = () => {
        const selectedDistrictCodes = filterState?.DistrictView.find(item => item.isChecked)?.filterCode;
        const selectedBrandCodes = filterState.BrandView.filter(item => item.isChecked).map(item => item.filterCode);
        const selectedAllocationCodes = filterState?.AllocationStatusView.find(item => item.isChecked)?.filterCode;
        const selectedDistrictname = filterState?.DistrictView.find(item => item.isChecked)?.filterName;
        const selectedBrandnames = filterState.BrandView.filter(item => item.isChecked).map(item => item.filterName);
        const selectedAllocationNames = filterState?.AllocationStatusView.find(item => item.isChecked )?.filterName;

        const currentAccountName = currentAccountFilters.find((item)=> item.filterCode === filterState.currentLocationAccount)?.filterName;
        const homeAccountName = homeAccountFilters.find((item)=> item.filterCode === filterState.homeLocationAccount)?.filterName;
        const currentTerritoryName = territoryFilters.find((item)=> item.filterCode === filterState.currentLocationTerritory)?.filterName;
        const homeTerritoryName = territoryFilters.find((item)=> item.filterCode === filterState.homeLocationTerritory)?.filterName;

        const appliedFilterNames = [
            districtFilters.length > 1 && selectedDistrictname !== 'All' ? selectedDistrictname : '',
            brandFilterList.length > 1 && selectedBrandnames.length > 0 ? selectedBrandnames : '',
            shelfLifeFilterList.length > 1 && selectedAllocationNames !== t('filter.allSetsItemsLabel') ? selectedAllocationNames : '',
            currentAccountFilters.length > 0 && filterState.currentLocationAccount !== 'All' && filterState.currentLocationAccount !== undefined ? `Current Account : ${currentAccountName}` : '',
            homeAccountFilters.length > 0 && filterState.homeLocationAccount !== 'All' && filterState.homeLocationAccount !== undefined ? `Home Account : ${homeAccountName}` : '',
            territoryFilters.length > 1 && filterState.currentLocationTerritory !== 'All' && filterState.currentLocationTerritory !== undefined ? `Current Territory : ${currentTerritoryName}` : '',
            territoryFilters.length > 1 && filterState.homeLocationTerritory !== 'All' && filterState.homeLocationTerritory !== undefined ? `Home Territory : ${homeTerritoryName}` : ''
        ].filter(Boolean);

        const appliedFilters = {
            currentDistrict: selectedDistrictCodes !== 'All' ? selectedDistrictCodes : '',
            brand: selectedBrandCodes,
            shellLife: selectedAllocationCodes !== 'All' ? selectedAllocationCodes : '',
            currentAccountId: filterState.currentLocationAccount !== 'All' ? filterState.currentLocationAccount : '',
            homeAccountId: filterState.homeLocationAccount !== 'All' ? filterState.homeLocationAccount : '',
            currentTerritoryId: filterState.currentLocationTerritory !== 'All' ? filterState.currentLocationTerritory : '',
            homeTerritoryId: filterState.homeLocationTerritory !== 'All' ? filterState.homeLocationTerritory : '',
            appliedFilterNames
        };

        setPreFilterState({ ...filterState });
        const filtersForApi = { ...appliedFilters };
        delete filtersForApi.appliedFilterNames;
        delete setsSearchParams.currentDistrict;
        dispatch(getInventoryList({
            ...filtersForApi,
            ...setsSearchParams,
        }));
        dispatch(setSetsFilterData(appliedFilters));
        onClose();
    };

    const handleCancel = () => {
        setFilterState({ ...preFilterState });
        if (onClose) onClose();
    };

    // Check if filters are applied
    const isDistrictFilterApplied = useMemo(() => checkNonDefault(filterState.DistrictView), [filterState.DistrictView]);
    const isAllocationFilterApplied = useMemo(() => checkNonDefault(filterState.AllocationStatusView), [filterState.AllocationStatusView]);
     
    const isHomeLocationFilterApplied  = () =>{
        if(territoryFilters.length > 1 && filterState.homeLocationTerritory !=='All' && filterState.homeLocationTerritory !== undefined){
            return true;
        }
        if(homeAccountFilters.length > 0 && filterState.homeLocationAccount !=='All' && filterState.homeLocationAccount !== undefined){
            return true;
        }
        return false;
    };

    const isCurrentLocationFilterApplied  = () =>{
        if(territoryFilters.length > 1 &&   filterState.currentLocationTerritory !=='All' && filterState.currentLocationTerritory !== undefined){
            return true;
        }
        if(currentAccountFilters.length > 0 && filterState.currentLocationAccount !=='All' && filterState.currentLocationAccount !== undefined){
            return true;
        }
        return false;
    };

    // Check if any filter has changed
    const hasFilterChanges = useMemo(() => {
        return JSON.stringify(filterState) !== JSON.stringify(preFilterState);
    }, [filterState, preFilterState]);

    const handleSearchTextChange = (value) => {
        // SearchBox passes the typed text directly, not an event.
        dispatch(setSetsSearchbarText({ filterType: 'districtFilters', value }));
    };

    const filteredDistrictView = useMemo(() => {
        const districtSearchText = searchbarText.districtFilters || '';
        if (!districtSearchText) return filterState.DistrictView;
        const searchLower = districtSearchText.toLowerCase();
        return filterState.DistrictView.filter(
            (item) =>
                item.filterCode === 'All' ||
                item.filterName?.toLowerCase().includes(searchLower)
        );
    }, [filterState.DistrictView, searchbarText.districtFilters]);

    useEffectWithErrorLogging(() => {
        if (!districtFilters?.length && 
            !territoryFilters?.length && 
            !accountFilters?.length && 
            !brandFilterList?.length && 
            !shelfLifeFilterList?.length) 
                return;

        const newState = {
            DistrictView: buildFilterState({ list: districtFilters, appliedFilter: currentDistrict, allCode: 'All'}),
            BrandView: buildFilterState({ list: brandFilterList, appliedFilter: brand, allCode: 'All' }),
            AllocationStatusView: buildFilterState({ list: shelfLifeFilterList, appliedFilter: shellLife, allCode: 'All' }),
            homeLocationTerritory: homeTerritoryId || (territoryFilters.length > 0 ? territoryFilters[0].filterCode : 'All'),
            homeLocationAccount: homeAccountId || (accountFilters.length > 0 ? accountFilters[0].filterCode : 'All'),
            currentLocationTerritory: currentTerritoryId || (territoryFilters.length > 0 ? territoryFilters[0].filterCode : 'All'),
            currentLocationAccount: currentAccountId || (accountFilters.length > 0 ? accountFilters[0].filterCode : 'All'),
        };
        setFilterState(newState);
        setPreFilterState(newState);
    }, []);

    return (
        <FilterPopup bannerHeight={bannerHeight} viewportHeight={viewportHeight}>
            <FilterHeader>
                <FilterHeading>Filters</FilterHeading>
                <Reset onClick={handleReset} disabled={!isFilterApplied} >Reset</Reset>
            </FilterHeader>
            <FilterContent>
                <CommonFilter
                    key="district"
                    header={t('filter.districtLabel')}
                    filters={filteredDistrictView}
                    onCheckBoxChange={onCheckBoxChange}
                    isExpanded={expandedViews.DistrictView}
                    updateExpandedViews={updateExpandedViews}
                    allFiltersWithStatus=""
                    checkBoxName="DistrictView"
                    view="DistrictView"
                    isFilterApplied={isDistrictFilterApplied}
                    isRadio={true}
                    showSearchBar={districtFilters.length > 1}
                    updateSearch={handleSearchTextChange}
                    searchProduct={searchbarText.districtFilters || ''}
                     placeholderProp={t('admin.adminList.search') +
                                        ' ' +
                                        t('filter.districtLabel')
                                    }
                />
                
                <LocationFilter
                    title={t('filter.filterByHomeLocation')}
                    viewKey="HomeLocationView"
                    territoryField={"homeLocationTerritory"}
                    accountField={"homeLocationAccount"}
                    isApplied={isHomeLocationFilterApplied()}
                    isExpanded={expandedViews.HomeLocationView}
                    filterState={filterState}
                    updateExpandedViews={updateExpandedViews}
                    districtCode={filterState?.DistrictView.find(item => item.isChecked && item.filterCode !== 'All')?.filterCode}
                    territoryFilters={territoryFilters}
                    accountFilters={homeAccountFilters}
                    handleLocationDropdownChange={handleLocationDropdownChange}   
                />
                
                <LocationFilter
                    title={t('filter.filterByCurrentLocation')}
                    viewKey="CurrentLocationView"
                    territoryField={"currentLocationTerritory"}
                    accountField={"currentLocationAccount"}
                    isApplied={isCurrentLocationFilterApplied()}
                    isExpanded={expandedViews.CurrentLocationView}
                    filterState={filterState}
                    updateExpandedViews={updateExpandedViews}
                    districtCode={filterState?.DistrictView.find(item => item.isChecked && item.filterCode !== 'All')?.filterCode}
                    territoryFilters={territoryFilters}
                    accountFilters={currentAccountFilters}
                    handleLocationDropdownChange={handleLocationDropdownChange}
                />

                <BrandFilter
                    key="brandFilter"
                    header={t('filter.filterByBrand')}
                    filters={filterState.BrandView}
                    onCheckBoxChange={onCheckBoxChange}
                    isExpanded={expandedViews.BrandView}
                    updateExpandedViews={updateExpandedViews}
                    checkBoxName="BrandView"
                    view="BrandView"
                    codeKey="filterCode"
                    nameKey="filterName"
                    persistSearchOnToggle
                />
                
                <CommonFilter
                    key="allocationStatus"
                    header={t('filter.filterByAllocationStatus')}
                    filters={filterState.AllocationStatusView}
                    onCheckBoxChange={onCheckBoxChange}
                    isExpanded={expandedViews.AllocationStatusView}
                    updateExpandedViews={updateExpandedViews}
                    allFiltersWithStatus=""
                    checkBoxName="AllocationStatusView"
                    view="AllocationStatusView"
                    isFilterApplied={isAllocationFilterApplied}
                    isRadio={true}
                />
            </FilterContent>
            
            <BottomNavigation
                justifyContent="space-evenly"
                opacity="1"
                position="sticky"
                padding="11px"
                setsFilterFooter
            >
                <Button
                    id="sets-filter-cancel-button"
                    show={true}
                    variant={ButtonStyles?.secondary}
                    width="40%"
                    height="38px"
                    clicked={handleCancel}
                >
                    {t('common.closeButton')}
                </Button>
                <Button
                    id="sets-filter-apply-button"
                    show={true}
                    variant={hasFilterChanges ? ButtonStyles?.primary : ButtonStyles?.disabled}
                    width="40%"
                    height="38px"
                    clicked={handleApply}
                    disabled={!hasFilterChanges}
                >
                    {t('common.applyButton')}
                </Button>
            </BottomNavigation>
        </FilterPopup>
    );
};

export default SetsFilterContainer;
